import { mapOnboardingStatus } from '@/services/onboarding/onboarding.mappers';
import type { UseOnboardingStatusParams } from '@/services/onboarding/onboarding.types';
import { gateStatusQueryOptions } from '@/services/statusGate/useStatusGate';
import type { OnboardingStatus, OnboardingStatusResponse } from '@/types/onboarding';
import { useQuery } from '@tanstack/react-query';

/** Scoped per auth account so a cached status never leaks across users (e.g. impersonation). */
export const onboardingStatusQueryKey = (accountId: string | null) => ['onboarding-status', accountId] as const;

export const fetchOnboardingStatus = async (): Promise<OnboardingStatus> => {
  const response = await fetch('/api/onboarding/status', {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to check onboarding status');
  const body: OnboardingStatusResponse = await response.json();
  return mapOnboardingStatus(body);
};

/** Shared by the guard and the onboarding page (pre-fill). */
export const onboardingStatusQueryOptions = (accountId: string | null) =>
  gateStatusQueryOptions(onboardingStatusQueryKey(accountId), fetchOnboardingStatus);

export const useOnboardingStatus = ({ accountId, enabled }: UseOnboardingStatusParams) =>
  useQuery({ ...onboardingStatusQueryOptions(accountId), enabled });
